import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Lightbulb, TrendingUp, TrendingDown, AlertTriangle, Activity, Database,
  BellPlus, BellRing, Loader2, RefreshCw, X, Mail, SearchCheck, Trash2, Power,
  MessageSquare, Sparkles as SparklesIcon, Pencil, CalendarClock, Archive,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SchedulePicker, { defaultScheduleSpec } from '@/components/SchedulePicker';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { EditCalcMeasureDialog } from '@/components/semantic-models/EditCalcMeasureDialog';
import QuickEditFieldDrawer from '@/components/explore/QuickEditFieldDrawer';
import { useBrandName, getTerm, useTerms } from '@/lib/branding';
import { InvestigatePanel } from '@/components/investigate/InvestigatePanel';
import ObjectChatDialog from '@/components/ObjectChatDialog';
import MorningBriefCard from '@/components/MorningBriefCard';

const TYPE_META = {
  trend_up:            { label: 'Trend',        icon: TrendingUp,   cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  trend_down:          { label: 'Trend',        icon: TrendingDown, cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  anomaly:             { label: 'Anomaly',      icon: Activity,     cls: 'bg-rose-500/10 text-rose-400 border-rose-500/20' },
  concentration:       { label: 'Concentration',icon: AlertTriangle,cls: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
  data_health:         { label: 'Data health',  icon: Database,     cls: 'bg-sky-500/10 text-sky-400 border-sky-500/20' },
  alert_recommendation:{ label: 'Alert idea',   icon: BellPlus,     cls: 'bg-violet-500/10 text-violet-400 border-violet-500/20' },
  rca:                 { label: 'Root cause',   icon: SearchCheck,  cls: 'bg-red-500/10 text-red-400 border-red-500/20' },
  certification_recommendation: { label: 'Certify', icon: Lightbulb, cls: 'bg-teal-500/10 text-teal-400 border-teal-500/20' },
  predictive_breach:   { label: 'Projected breach', icon: TrendingUp, cls: 'bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/20' },
  stale_content:       { label: 'Stale content', icon: CalendarClock, cls: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' },
};

const SEV_DOT = { critical: 'bg-rose-500', warning: 'bg-amber-400', info: 'bg-sky-400' };

const InsightCard = ({ ins, isAdmin, onDismiss, onAcceptAlert, onCreateAlert, onStartThread, onInvestigate, onCertify, onQuickEdit, onRescan, onOpen, onArchive, modelStale, rescanning, busy, highlight, cardRef, discCount = 0, onOpenDiscussion }) => {
  const meta = TYPE_META[ins.type] || TYPE_META.data_health;
  const Icon = meta.icon;
  return (
    <div
      ref={cardRef}
      className={`rounded-xl border bg-card p-4 hover:border-primary/30 transition-colors ${highlight ? 'ring-2 ring-primary/40 border-primary/40' : ''}`}
      data-testid="insight-card"
      data-insight-id={ins.insight_id}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${SEV_DOT[ins.severity] || SEV_DOT.info}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge variant="outline" className={`text-[9px] ${meta.cls}`}><Icon className="w-2.5 h-2.5 mr-1" />{meta.label}</Badge>
              {ins.status === 'accepted' && <Badge variant="outline" className="text-[9px] bg-emerald-500/10 text-emerald-400">Alert active</Badge>}
              {ins.type === 'predictive_breach' && ins.evidence?.confidence && (
                <Badge className={`text-[9px] ${ins.evidence.confidence === 'High' ? 'bg-emerald-500/15 text-emerald-300' : ins.evidence.confidence === 'Medium' ? 'bg-amber-500/15 text-amber-300' : 'bg-rose-500/15 text-rose-300'}`} data-testid={`insight-confidence-${ins.insight_id}`} title={`Forecast confidence from backtest error (MAPE ${ins.evidence.mape ?? '—'}%)`}>{ins.evidence.confidence} confidence</Badge>
              )}
              {discCount > 0 && (
                <Badge variant="outline" className="text-[9px] bg-primary/10 text-primary border-primary/20 cursor-pointer" onClick={() => onOpenDiscussion(ins)} data-testid={`insight-discussion-indicator-${ins.insight_id}`}>
                  <MessageSquare className="w-2.5 h-2.5 mr-1" />{discCount} discussion{discCount === 1 ? '' : 's'}
                </Badge>
              )}
              <span className="text-[10px] text-muted-foreground">{new Date(ins.created_at).toLocaleDateString()}</span>
            </div>
            <h3 className="text-sm font-semibold leading-snug" data-testid="insight-title">{ins.title}</h3>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed whitespace-pre-line">{ins.summary}</p>
            {ins.type === 'rca' && ins.evidence?.top_contributors?.length > 0 && (
              <div className="flex gap-1.5 flex-wrap mt-2">
                {ins.evidence.top_contributors.map((c, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-muted/60 font-mono">
                    {c.dim}={c.value} ({c.contribution_pct}%)
                  </span>
                ))}
              </div>
            )}
            {modelStale && (
              <div className="flex items-center gap-2 mt-2 px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 w-fit" data-testid={`insight-resync-badge-${ins.insight_id}`}>
                <span className="text-[10px] text-amber-400 font-medium">Model updated</span>
                <button
                  onClick={() => onRescan(ins)}
                  disabled={rescanning}
                  className="text-[10px] text-amber-300 underline underline-offset-2 hover:text-amber-200 disabled:opacity-50 flex items-center gap-1"
                  data-testid={`insight-resync-rescan-${ins.insight_id}`}
                >
                  {rescanning ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RefreshCw className="w-2.5 h-2.5" />} Rescan to apply
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          {ins.type === 'certification_recommendation' && ins.status !== 'dismissed' && (
            <Button size="sm" className="h-7 text-[10px]" disabled={busy === ins.insight_id}
              onClick={() => onCertify(ins)} data-testid={`certify-insight-${ins.insight_id}`}>
              {busy === ins.insight_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Lightbulb className="w-3 h-3 mr-1" /> Certify</>}
            </Button>
          )}
          {isAdmin && ins.recommended_alert && ins.status !== 'accepted' && (
            <Button size="sm" className="h-7 text-[10px]" disabled={busy === ins.insight_id}
              onClick={() => onAcceptAlert(ins.insight_id)} data-testid="accept-alert-btn">
              {busy === ins.insight_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><BellPlus className="w-3 h-3 mr-1" /> Add alert</>}
            </Button>
          )}
          {ins.type === 'forecast_projected_issue' && ins.evidence?.measure && (
            <Button size="sm" className="h-7 text-[10px]" onClick={() => onCreateAlert(ins)} data-testid={`create-alert-${ins.insight_id}`}>
              <BellPlus className="w-3 h-3 mr-1" /> Create alert
            </Button>
          )}
          {ins.type === 'stale_content' && ins.evidence?.report_id && (
            <>
              <Button size="sm" className="h-7 text-[10px]"
                onClick={() => onOpen(ins)} data-testid={`open-stale-${ins.insight_id}`}>
                <SparklesIcon className="w-3 h-3 mr-1" /> Open {ins.evidence?.kind || 'report'}
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-[10px] border-amber-500/30 text-amber-600 dark:text-amber-300 hover:bg-amber-500/10"
                onClick={() => onArchive(ins)} data-testid={`archive-stale-${ins.insight_id}`}>
                <Archive className="w-3 h-3 mr-1" /> Archive
              </Button>
            </>
          )}
          {ins.type !== 'stale_content' && (
            <Button variant="outline" size="sm" className="h-7 text-[10px] border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10"
              onClick={() => onInvestigate(ins)} data-testid={`investigate-insight-${ins.insight_id}`}>
              <SparklesIcon className="w-3 h-3 mr-1" /> Investigate
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-7 text-[10px] text-muted-foreground"
            onClick={() => onStartThread(ins)} data-testid={`start-thread-${ins.insight_id}`}>
            <MessageSquare className="w-3 h-3 mr-1" /> Start a Discussion
          </Button>
          {ins.model_id && ins.type !== 'stale_content' && (
            <Button variant="ghost" size="sm" className="h-7 text-[10px] text-muted-foreground"
              onClick={() => onQuickEdit(ins)} data-testid={`insight-quick-edit-${ins.insight_id}`} title="Quick-edit a field in this insight's model">
              <Pencil className="w-3 h-3 mr-1" /> Edit field
            </Button>
          )}
          {ins.status !== 'dismissed' && ins.status !== 'accepted' && (
            <Button variant="ghost" size="sm" className="h-7 text-[10px] text-muted-foreground"
              onClick={() => onDismiss(ins.insight_id)} data-testid="dismiss-insight-btn">
              <X className="w-3 h-3 mr-1" /> Dismiss
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function InsightsFeedPage() {
  const APP = useBrandName();
  useTerms();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ['admin', 'tenant_admin', 'super_admin'].includes(user?.role);
  const [searchParams] = useSearchParams();
  const [insights, setInsights] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [filter, setFilter] = useState('all');
  const highlightInsightId = searchParams.get('insight_id') || '';
  const cardRefs = useRef({});
  const [investigateOpen, setInvestigateOpen] = useState(false);
  const [investigateTarget, setInvestigateTarget] = useState(null);
  const [discIndex, setDiscIndex] = useState({});
  const [threadDialog, setThreadDialog] = useState({ open: false, ins: null });

  // Certify-via-Edit-Calculated-Measure dialog state.
  const [certDlgOpen, setCertDlgOpen] = useState(false);
  const [certIns, setCertIns] = useState(null);
  const [certEntities, setCertEntities] = useState([]);
  const [ecName, setEcName] = useState('');
  const [ecFormula, setEcFormula] = useState('');
  const [ecDesc, setEcDesc] = useState('');
  const [ecEntity, setEcEntity] = useState('');
  const [ecCursor, setEcCursor] = useState(0);
  const [ecMeasure, setEcMeasure] = useState(null);

  // Quick-Edit field drawer (mirrors AI Explore Phase 1) + model-updated badge.
  const [quickEdit, setQuickEdit] = useState({ open: false, modelId: '', fields: [] });
  const [modelEditedAt, setModelEditedAt] = useState({}); // { [model_id]: timestamp }

  const openQuickEdit = async (ins) => {
    if (!ins?.model_id) return;
    let fields = [];
    try {
      const full = await api.getSemanticModelFull(ins.model_id);
      const seen = {};
      (full.entities || []).forEach(e => (e.attributes || []).forEach(a => {
        if (a.name && !seen[a.name]) { seen[a.name] = 1; fields.push({ name: a.name, label: a.business_name || a.display_name || a.name }); }
      }));
    } catch { /* drawer still opens; it loads entities itself */ }
    setQuickEdit({ open: true, modelId: ins.model_id, fields });
  };

  const onFieldSaved = () => {
    if (quickEdit.modelId) setModelEditedAt(prev => ({ ...prev, [quickEdit.modelId]: Date.now() }));
  };

  const rescanForInsight = async (ins) => {
    await runScan();
    if (ins?.model_id) setModelEditedAt(prev => { const n = { ...prev }; delete n[ins.model_id]; return n; });
  };

  const openInvestigate = (ins) => {
    const ev = ins.evidence || {};
    if (ins.type === 'forecast_projected_issue') {
      // Projected-issue cards carry the full forecast context — pass it so the
      // investigation opens ready to run on WHY the issue is projected.
      const dir = ev.direction === 'down' ? 'decline' : ev.direction === 'up' ? 'increase' : 'change';
      const val = ev.projected_value != null ? Number(ev.projected_value).toLocaleString() : 'the projected level';
      const mlabel = ev.measure_label || ev.measure || ins.measure;
      const q = [
        `Investigate why ${mlabel} is forecast to ${dir} to ${val} by ${ev.projected_period || 'the projected period'}.`,
        ins.summary || '',
        `The forecast used the ${ev.model || 'auto-selected'} model at ${ev.confidence || 'unknown'} confidence` +
          `${ev.mape != null ? ` (backtest error ~${Math.round(ev.mape)}% MAPE)` : ''}.`,
        `Identify the most likely drivers and root causes behind this projected ${dir}, using the underlying data.`,
      ].filter(Boolean).join(' ');
      setInvestigateTarget({
        metric: ev.measure || ins.measure, model_id: ev.model_id || ins.model_id,
        measure_label: mlabel, source: 'forecast_sentinel', insight_id: ins.insight_id,
        projected_period: ev.projected_period, projected_value: ev.projected_value,
        direction: ev.direction, confidence: ev.confidence, model: ev.model, mape: ev.mape,
        sentinel_id: ev.sentinel_id, question: q, filters: {},
      });
      setInvestigateOpen(true);
      return;
    }
    setInvestigateTarget({
      metric: ins.metric || ins.evidence?.metric,
      model_id: ins.model_id,
      window: ins.window || 'the last period',
      direction: (ins.type === 'drop' || ins.severity === 'critical') ? 'drop' : 'rise',
      source: 'insight',
      insight_id: ins.insight_id,
      insight_title: ins.title,
      insight_summary: ins.summary,
      // Prefill the investigation question from the insight so it opens ready to run.
      question: ins.title ? `Investigate: ${ins.title}` : undefined,
      filters: ins.evidence?.filters || {},
    });
    setInvestigateOpen(true);
  };

  // When arriving from a discussion deep-link, scroll to and highlight
  // the referenced insight so the reader immediately sees exactly what
  // the discussion is about.
  useEffect(() => {
    if (!highlightInsightId || loading) return;
    const el = cardRefs.current[highlightInsightId];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightInsightId, loading, insights]);

  // Open the object-attached chat for this insight (replaces legacy Discussions).
  const startInsightThread = (ins) => setThreadDialog({ open: true, ins });
  const openExistingInsightThread = (ins) => setThreadDialog({ open: true, ins });

  const loadDiscIndex = useCallback(() => {
    api.getDiscussionIndex().then(r => setDiscIndex(r?.by_insight || {})).catch(() => {});
  }, []);
  useEffect(() => { loadDiscIndex(); }, [loadDiscIndex]);

  const [scanStatus, setScanStatus] = useState(null);
  const load = useCallback(async () => {
    try {
      const [f, a] = await Promise.all([api.getInsightsFeed(), api.getAiAlerts()]);
      setInsights(f.insights || []);
      setAlerts(a.alerts || []);
    } catch (e) { toast.error(e.message); }
    try { setScanStatus(await api.getInsightsScanStatus()); } catch { /* optional */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setBusy('scan');
    try {
      const r = await api.runInsightsScan();
      toast.success(r.created ? `Scan complete — ${r.created} new finding(s)` : 'Scan complete — nothing new since last scan');
      await load();
    } catch (e) { toast.error(e.message); }
    setBusy('');
  };

  // Auto-scan schedule (shared unified scheduler)
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scanSpec, setScanSpec] = useState(defaultScheduleSpec());
  const [scanEnabled, setScanEnabled] = useState(false);
  const [scanLabel, setScanLabel] = useState(null);
  const [scanNextRun, setScanNextRun] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);

  const openSchedule = async () => {
    setScheduleOpen(true);
    try {
      const s = await api.getInsightsScanSchedule();
      setScanEnabled(!!s.enabled);
      setScanSpec(s.schedule?.mode ? s.schedule : defaultScheduleSpec());
      setScanLabel(s.schedule_label || null);
      setScanNextRun(s.next_run || null);
    } catch { /* first-time, use defaults */ }
  };
  const saveSchedule = async (enable) => {
    setSavingSchedule(true);
    try {
      const r = await api.setInsightsScanSchedule({ enabled: enable, schedule: scanSpec });
      setScanEnabled(!!r.enabled);
      setScanLabel(r.schedule_label || null);
      setScanNextRun(r.next_run || null);
      toast.success(enable ? 'Auto-scan schedule saved' : 'Auto-scan turned off');
      if (!enable) setScheduleOpen(false);
    } catch (e) { toast.error(e.message); }
    setSavingSchedule(false);
  };

  const evaluateAlerts = async () => {
    setBusy('eval');
    try {
      const r = await api.evaluateAiAlerts();
      toast.success(r.triggered ? `${r.triggered} alert(s) triggered — RCA added to the feed` : 'All watched metrics are within normal range');
      await load();
    } catch (e) { toast.error(e.message); }
    setBusy('');
  };

  const sendDigest = async () => {
    setBusy('digest');
    try {
      const r = await api.sendInsightsDigest();
      toast.success(r.findings_emailed ? `Digest emailed (${r.findings_emailed} findings)` : 'No new findings to email');
    } catch (e) { toast.error(e.message); }
    setBusy('');
  };

  const dismiss = async (id) => {
    try { await api.dismissInsight(id); setInsights(xs => xs.map(x => x.insight_id === id ? { ...x, status: 'dismissed' } : x)); }
    catch (e) { toast.error(e.message); }
  };

  const acceptAlert = async (id) => {
    setBusy(id);
    try {
      const r = await api.acceptInsightAlert(id);
      toast.success(r.message || 'Alert created');
      await load();
    } catch (e) { toast.error(e.message); }
    setBusy('');
  };

  const certify = async (ins) => {
    // Open the full Edit Calculated Measure editor pre-filled from the insight;
    // saving creates the certified measure in the associated model.
    const ev = ins.evidence || {};
    setCertIns(ins);
    setEcName(ev.measure || ins.measure || '');
    setEcFormula(ev.formula || '');
    setEcDesc(ev.formula ? `Ad-hoc calculation: ${ev.formula}` : '');
    setEcCursor((ev.formula || '').length);
    setEcMeasure({ measure_id: ins.insight_id, name: ev.measure || ins.measure || '', formula: ev.formula || '', description: ev.formula ? `Ad-hoc calculation: ${ev.formula}` : '' });
    setCertEntities([]);
    setEcEntity('');
    try {
      const ents = await api.getSemanticEntities(ins.model_id);
      const list = Array.isArray(ents) ? ents : (ents?.entities || []);
      setCertEntities(list);
      setEcEntity((list[0])?.entity_id || '');
    } catch { /* dialog still opens */ }
    setCertDlgOpen(true);
  };

  const saveCertifiedMeasure = async (payload) => {
    if (!certIns) return;
    // Create the request, then approve it → creates the governed measure in the model.
    const reqRes = await api.requestExploreCertification({
      model_id: certIns.model_id, measure_name: payload.name,
      formula: payload.formula, business_definition: payload.description,
    });
    const reqId = reqRes?.request_id;
    try {
      if (reqId) await api.approveCertRequest(reqId, { name: payload.name, formula: payload.formula, business_definition: payload.description });
      toast.success(`'${payload.name}' certified and added to the model`);
    } catch {
      // Not a steward → the request is queued for review instead.
      toast.success('Sent to the steward review queue for certification');
    }
    await api.dismissInsight(certIns.insight_id);
    setInsights(xs => xs.map(x => x.insight_id === certIns.insight_id ? { ...x, status: 'dismissed' } : x));
    setCertIns(null);
  };

  const toggleAlert = async (id) => {
    try { const r = await api.toggleAiAlert(id); setAlerts(xs => xs.map(a => a.alert_id === id ? { ...a, enabled: r.enabled } : a)); }
    catch (e) { toast.error(e.message); }
  };

  const deleteAlert = async (id) => {
    try { await api.deleteAiAlert(id); setAlerts(xs => xs.filter(a => a.alert_id !== id)); toast.success('Alert removed'); }
    catch (e) { toast.error(e.message); }
  };

  const visible = insights.filter(i => {
    if (i.status === 'dismissed') return false;
    if (filter === 'all') return true;
    if (filter === 'alerts') return ['alert_recommendation', 'rca', 'predictive_breach'].includes(i.type);
    return i.type.startsWith(filter);
  });

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="insights-feed-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-primary" /> {getTerm('ai_insights')}
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">What {APP} noticed in your data — trends, anomalies, risks, and root causes.</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={runScan} disabled={!!busy} data-testid="run-scan-btn">
              {busy === 'scan' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Scan now</>}
            </Button>
            <Button variant="outline" size="sm" onClick={openSchedule} disabled={!!busy} data-testid="schedule-scans-btn">
              <CalendarClock className="w-3.5 h-3.5 mr-1" /> {scanEnabled ? 'Auto-scan: On' : 'Schedule scans'}
            </Button>
            <Button variant="outline" size="sm" onClick={evaluateAlerts} disabled={!!busy} data-testid="evaluate-alerts-btn">
              {busy === 'eval' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><BellRing className="w-3.5 h-3.5 mr-1" /> Check alerts</>}
            </Button>
            <Button variant="outline" size="sm" onClick={sendDigest} disabled={!!busy} data-testid="send-digest-btn">
              {busy === 'digest' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Mail className="w-3.5 h-3.5 mr-1" /> Email digest</>}
            </Button>
          </div>
        )}
      </div>

      <MorningBriefCard isAdmin={isAdmin} />

      {scanStatus && scanStatus.last_run && (
        <p className="text-[11px] text-muted-foreground -mt-3 mb-5" data-testid="scan-history">
          Last scan {new Date(scanStatus.last_run).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          {typeof scanStatus.last_created === 'number' ? ` · ${scanStatus.last_created} new finding${scanStatus.last_created === 1 ? '' : 's'}` : ''}
          {scanEnabled && scanNextRun ? ` · next auto-scan ${new Date(scanNextRun).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
        </p>
      )}

      {alerts.length > 0 && (
        <div className="rounded-xl border bg-card/60 p-4 mb-6" data-testid="ai-alerts-panel">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Watched metrics ({alerts.length})</h2>
          <div className="space-y-2">
            {alerts.map(a => (
              <div key={a.alert_id} className="flex items-center justify-between gap-3 text-xs" data-testid="ai-alert-row">
                <div className="flex items-center gap-2 min-w-0">
                  <BellRing className={`w-3.5 h-3.5 flex-shrink-0 ${a.enabled ? 'text-primary' : 'text-muted-foreground/70'}`} />
                  <span className={a.enabled ? '' : 'text-muted-foreground line-through'}>
                    {a.measure_label || a.measure} <span className="text-muted-foreground">({a.entity_name}) — alert on drop &gt;{a.threshold_pct}%</span>
                  </span>
                  {a.last_triggered_period && <Badge variant="outline" className="text-[9px] bg-rose-500/10 text-rose-400">triggered {a.last_triggered_period}</Badge>}
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => toggleAlert(a.alert_id)} className="p-1 rounded hover:bg-muted text-muted-foreground" title={a.enabled ? 'Disable' : 'Enable'} data-testid="toggle-alert-btn"><Power className="w-3.5 h-3.5" /></button>
                    <button onClick={() => deleteAlert(a.alert_id)} className="p-1 rounded hover:bg-muted text-muted-foreground" title="Delete" data-testid="delete-alert-btn"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-1.5 mb-4 flex-wrap">
        {[['all', 'All'], ['trend', 'Trends'], ['anomaly', 'Anomalies'], ['concentration', 'Risks'], ['data_health', 'Data health'], ['alerts', 'Alerts & RCA']].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-2.5 py-1 rounded-full text-[11px] transition-colors ${filter === k ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground border border-border/50'}`}
            data-testid={`filter-${k}`}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
      ) : visible.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="insights-empty">
          <Lightbulb className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No {getTerm('ai_insights', 'plural', 'lower')} yet.</p>
          {isAdmin && <p className="text-xs mt-1">Click "Scan now" to have the AI review your data.</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map(ins => (
            <InsightCard
              key={ins.insight_id}
              ins={ins}
              isAdmin={isAdmin}
              busy={busy}
              onDismiss={dismiss}
              onAcceptAlert={acceptAlert}
              onCreateAlert={(ins) => {
                const e = ins.evidence || {};
                navigate('/alerts', { state: { prefillAlert: {
                  name: `Forecast: ${e.measure_label || e.measure}`,
                  metric: e.measure, model_id: e.model_id,
                  operator: e.operator || 'gt',
                  threshold: e.suggested_threshold ?? '',
                  severity: 'medium', predictive: true,
                  forecast_horizon: e.horizon || 6, forecast_unit: e.unit === 'quarter' || e.unit === 'year' ? e.unit : 'month',
                } } });
              }}
              onStartThread={startInsightThread}
              onOpenDiscussion={openExistingInsightThread}
              discCount={(discIndex[ins.insight_id]?.total) || 0}
              onInvestigate={openInvestigate}
              onCertify={certify}
              onQuickEdit={openQuickEdit}
              onRescan={rescanForInsight}
              onOpen={(ins) => {
                const e = ins.evidence || {};
                const rid = e.report_id || ins.model_id;
                navigate(`${e.kind === 'dashboard' ? '/dashboards/view' : '/reports/view'}?id=${rid}`);
              }}
              onArchive={async (ins) => {
                const rid = (ins.evidence || {}).report_id || ins.model_id;
                try {
                  await api.archiveReport(rid);
                  await api.dismissInsight(ins.insight_id);
                  setInsights(xs => xs.map(x => x.insight_id === ins.insight_id ? { ...x, status: 'dismissed' } : x));
                  toast.success('Archived — removed from your Insights list');
                } catch (e) { toast.error(e.message || 'Could not archive'); }
              }}
              rescanning={busy === 'scan'}
              modelStale={!!(ins.model_id && modelEditedAt[ins.model_id] && new Date(ins.created_at).getTime() < modelEditedAt[ins.model_id])}
              highlight={ins.insight_id === highlightInsightId}
              cardRef={(el) => { if (el) cardRefs.current[ins.insight_id] = el; }}
            />
          ))}
        </div>
      )}
      <InvestigatePanel open={investigateOpen} onOpenChange={setInvestigateOpen} target={investigateTarget} />
      <ObjectChatDialog
        open={threadDialog.open}
        onOpenChange={(v) => setThreadDialog(t => ({ ...t, open: v }))}
        objectType="insight"
        objectId={threadDialog.ins?.insight_id}
        label={threadDialog.ins?.title || 'Insight discussion'}
      />
      <EditCalcMeasureDialog
        open={certDlgOpen}
        onOpenChange={setCertDlgOpen}
        editCalcMeasure={ecMeasure}
        setEditCalcMeasure={setEcMeasure}
        editCalcName={ecName} setEditCalcName={setEcName}
        editCalcFormula={ecFormula} setEditCalcFormula={setEcFormula}
        editCalcDesc={ecDesc} setEditCalcDesc={setEcDesc}
        editCalcEntity={ecEntity} setEditCalcEntity={setEcEntity}
        editCalcCursorPos={ecCursor} setEditCalcCursorPos={setEcCursor}
        entities={certEntities}
        setCalcMeasures={() => {}}
        activeModelId={certIns?.model_id}
        onSave={saveCertifiedMeasure}
      />
      <QuickEditFieldDrawer
        open={quickEdit.open}
        onOpenChange={(v) => setQuickEdit(q => ({ ...q, open: v }))}
        modelId={quickEdit.modelId}
        fields={quickEdit.fields}
        onSaved={onFieldSaved}
      />

      {/* Auto-scan schedule (shared unified scheduler) */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-md" data-testid="insights-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarClock className="w-5 h-5 text-primary" /> Auto-scan schedule</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Run the AI data-review scan automatically on a cadence. Findings appear in this feed and admins get the digest email.</p>
          {scanEnabled && scanLabel && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-400" data-testid="insights-schedule-status">
              Active: {scanLabel}{scanNextRun ? ` · next ${new Date(scanNextRun).toLocaleString()}` : ''}
            </div>
          )}
          <SchedulePicker value={scanSpec} onChange={setScanSpec} />
          <div className="flex justify-between gap-2 pt-2">
            {scanEnabled ? (
              <Button variant="outline" onClick={() => saveSchedule(false)} disabled={savingSchedule} data-testid="insights-schedule-disable-btn">Turn off</Button>
            ) : <span />}
            <Button onClick={() => saveSchedule(true)} disabled={savingSchedule} className="gap-2" data-testid="insights-schedule-save-btn">
              {savingSchedule ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {scanEnabled ? 'Update schedule' : 'Enable auto-scan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
